import { when } from 'mobx';
import { useEffect, useState } from 'react';

import type { Goal as GoalModel, IPredicate } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

export type TFormSelection = 'any' | 'specific';

interface IUseFormGoalSegmentParams {
  goal: GoalModel;
  buildPredicates: (formIds?: string[]) => IPredicate[];
  segmentNamePrefix: string;
}

interface IUseFormGoalSegmentReturn {
  selectMode: TFormSelection;
  selectedForms: string[];
  isLoaded: boolean;
  handleSelectMode: (value?: string) => void;
  handleFormSelection: (value?: string[]) => void;
}

/**
 * Manages segment loading, creation, and form selection state for form-based goals.
 *
 * @param params.goal - The goal model instance.
 * @param params.buildPredicates - Function to build predicates from selected form IDs.
 * @param params.segmentNamePrefix - Prefix for the segment name (e.g. 'Form Submission Goal').
 * @returns State and handlers for form goal segment management.
 */
export function useFormGoalSegment({
  goal,
  buildPredicates,
  segmentNamePrefix,
}: IUseFormGoalSegmentParams): IUseFormGoalSegmentReturn {
  const { Segments } = useStore();
  const [selectMode, setSelectMode] = useState<TFormSelection>('any');
  const [selectedForms, setSelectedForms] = useState<string[]>([]);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    async function loadExistingSegment(): Promise<void> {
      const segmentId = goal.get('segment');
      if (!segmentId) {
        setIsLoaded(true);
        return;
      }
      const segment = Segments.get(segmentId);
      await when(() => segment.isFetched);
      const predicates = segment.get('predicates');

      if (!predicates?.length) {
        setIsLoaded(true);
        return;
      }

      const innerGroup = predicates[0]?.group;
      if (!innerGroup?.length) {
        setIsLoaded(true);
        return;
      }

      const formIdPredicate = innerGroup.find((p) => p.attr_against === 'form_id');
      if (formIdPredicate?.value && Array.isArray(formIdPredicate.value)) {
        setSelectMode('specific');
        setSelectedForms(formIdPredicate.value);
      }

      setIsLoaded(true);
    }

    loadExistingSegment();
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    async function updateSegment(): Promise<void> {
      const formIds = selectMode === 'specific' ? selectedForms : undefined;
      const predicates = buildPredicates(formIds);
      const segmentName = `${segmentNamePrefix} - ${goal.get('name') || 'Unnamed'}`;

      const result = await Segments.getOrCreateGoalSegment(predicates, segmentName);
      goal.set({ segment: result.segment.id });
    }

    if (selectMode === 'any' || (selectMode === 'specific' && selectedForms.length > 0)) {
      updateSegment();
    } else {
      // Specific forms mode with no forms selected yet is an incomplete goal;
      // clear the segment so it fails validation and the wizard's Next button stays disabled.
      goal.unset('segment');
    }
  }, [selectMode, selectedForms, Segments, goal, isLoaded]);

  function handleSelectMode(value?: string): void {
    if (!value) {
      return;
    }
    setSelectMode(value as TFormSelection);
  }

  function handleFormSelection(value?: string[]): void {
    if (!value) {
      return;
    }
    setSelectedForms(value);
  }

  return {
    selectMode,
    selectedForms,
    isLoaded,
    handleSelectMode,
    handleFormSelection,
  };
}
